import { useSize } from 'ahooks'
import { motion, useDragControls } from 'framer-motion'
import React, { useRef } from 'react'

import useResizeHandlers from './_hooks/useResizeHandlers'

import type { WaveformProps } from '@/components/Waveform'

import DragControlIcon from '@/assets/svg/lip-sync.svg'
import Waveform from '@/components/Waveform'


import { cls } from 'tagged-classnames-free';
export interface AudioRangeSelectorProps {
  audioDuration?: number
  /** Maximum selectable duration in seconds, defaults to 30 */
  maxSelectDuration?: number
  /** Minimum selectable duration in seconds, defaults to maxSelectDuration (fixed width) */
  minSelectDuration?: number
  onRangeChange?: (start: number, end: number) => void
  playing?: boolean
  audioCurrentTime?: number | null
  fileName: string
  waveformProps?: WaveformProps
}

const AudioRangeSelector: React.FC<AudioRangeSelectorProps> = (props) => {
  const {
    audioDuration = 0,
    maxSelectDuration = 30,
    minSelectDuration,
    onRangeChange,
    audioCurrentTime,
    playing,
    fileName,
    waveformProps,
  } = props
  // 当未指定 minSelectDuration 时，选区宽度固定为 maxSelectDuration（原有行为）
  const effectiveMinDuration = minSelectDuration ?? maxSelectDuration
  const isResizable = effectiveMinDuration < maxSelectDuration
  const containerRef = useRef<HTMLDivElement>(null)
  const dragControls = useDragControls()
  const containerSize = useSize(containerRef)
  const startRef = useRef(0)

  const safeWidth = 0
  const trackLength = containerSize?.width || 0
  const defaultHandleWidth = Math.min(
    (Math.min(audioDuration, maxSelectDuration) / audioDuration) * trackLength +
      safeWidth,
    trackLength,
  )
  const minWidth = Math.min(
    (Math.min(audioDuration, effectiveMinDuration) / audioDuration) *
      trackLength +
      safeWidth,
    trackLength,
  )

  const handleResize = (offset: number, width: number) => {
    const start = (offset / trackLength) * audioDuration
    startRef.current = start
    const end = ((offset + width) / trackLength) * audioDuration
    onRangeChange?.(start, end)
  }

  const { dragOffset, dragSliderWidth, handleLeftResize, handleRightResize } =
    useResizeHandlers(defaultHandleWidth, {
      minWidth,
      maxWidth: defaultHandleWidth,
      trackLength,
      onResize: (offset, width) => {
        handleResize(offset, width)
      },
    })
  return (
    <motion.div ref={containerRef} className='relative size-full h-14 w-full'>
      <div
        className={cls`pointer-events-none absolute left-0 top-0 h-fit w-full select-none`}
        onClickCapture={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        <Waveform
          {...waveformProps}
          audio={{
            waveColor: '#656071',
            progressColor: '#302B42',
            height: 56,
            ...waveformProps?.audio,
          }}
        />
      </div>
      <motion.div
        dragMomentum={false}
        dragConstraints={containerRef}
        style={{
          x: dragOffset,
          width: dragSliderWidth,
          touchAction: 'none',
        }}
        drag='x'
        onDrag={() => {
          const offset = dragOffset.get()
          handleResize(offset, dragSliderWidth.get())
        }}
        dragElastic={0}
        dragListener={false}
        dragControls={dragControls}
        className='absolute inset-y-0 z-[2] rounded-md'
      >
        <div className='border-f-primary relative size-full rounded-md border bg-transparent'>
          <motion.span
            style={{
              touchAction: 'none',
            }}
            onPan={(event, info) => {
              if (isResizable) {
                handleLeftResize(event, info)
              }
            }}
            onPanStart={(event) => {
              if (!isResizable) {
                dragControls.start(event)
              }
            }}
            className={cls`
              bg-f-primary absolute inset-y-0 start-0 z-[3]
              flex w-1.5 items-center justify-center
              rounded-s-md ${isResizable ? 'hover:cursor-col-resize' : 'hover:cursor-move'}
            `}
          >
            <DragControlIcon className='h-3 w-1' />
          </motion.span>
          <motion.div
            onPanStart={(event) => {
              dragControls.start(event)
            }}
            className={cls`
              text-f-text-secondary
              flex size-full cursor-move items-center
              justify-start rounded-md bg-[#ff3466]
              bg-opacity-[10%] ps-2 text-xs
            `}
          >
            {/* <div className='before:i-cus--pol-sound absolute start-3 flex items-center gap-1 before:size-3'>
              {fileName && (
                <span className='block w-16 truncate'>{fileName}</span>
              )}
              <span>
                {playing &&
                  typeof audioCurrentTime === 'number' &&
                  `${formatMediaTime(audioCurrentTime - startRef.current, { pad: false })}s`}
              </span>
            </div> */}
          </motion.div>
          <motion.span
            style={{
              touchAction: 'none',
            }}
            onPan={(event, info) => {
              if (isResizable) {
                handleRightResize(event, info)
              }
            }}
            onPanStart={(event) => {
              if (!isResizable) {
                dragControls.start(event)
              }
            }}
            className={cls`
              bg-f-primary absolute inset-y-0 end-0 z-[3]
              flex w-1.5 items-center justify-center
              rounded-e-md ${isResizable ? 'hover:cursor-col-resize' : 'hover:cursor-move'}
            `}
          >
            <DragControlIcon className='h-3 w-1' />
          </motion.span>
        </div>
      </motion.div>
    </motion.div>
  )
}
export default AudioRangeSelector
